class GiftCardFormValidator {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form) return;

    this.recipientEmailInput = section.querySelector('#Recipient-email-' + this.getSectionId());
    this.senderEmailInput = section.querySelector('#Recipient-name-' + this.getSectionId());
    this.recipientError = section.querySelector('[data-field-error="recipient-email"]');
    this.senderError = section.querySelector('[data-field-error="sender-email"]');

    this.form.addEventListener('submit', this.validate.bind(this), true);
  }

  getSectionId() {
    var sectionEl = this.section.closest('[id^="shopify-section-"]');
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : '';
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  validate(evt) {
    var isValid = true;

    // Clear previous errors
    this.clearError(this.recipientError, this.recipientEmailInput);
    this.clearError(this.senderError, this.senderEmailInput);

    // Validate recipient email
    if (this.recipientEmailInput) {
      var recipientVal = this.recipientEmailInput.value.trim();
      if (!recipientVal || !this.isValidEmail(recipientVal)) {
        this.showError(this.recipientError, this.recipientEmailInput);
        isValid = false;
      }
    }

    // Validate sender email
    if (this.senderEmailInput) {
      var senderVal = this.senderEmailInput.value.trim();
      if (!senderVal || !this.isValidEmail(senderVal)) {
        this.showError(this.senderError, this.senderEmailInput);
        isValid = false;
      }
    }

    if (!isValid) {
      evt.preventDefault();
      evt.stopImmediatePropagation();

      // Scroll to first error field
      var firstError = this.section.querySelector('.gift-card-pdp__field-error:not(.hidden)');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  showError(errorEl, inputEl) {
    if (errorEl) errorEl.classList.remove('hidden');
    if (inputEl) inputEl.classList.add('gift-card-pdp__input--error');
  }

  clearError(errorEl, inputEl) {
    if (errorEl) errorEl.classList.add('hidden');
    if (inputEl) inputEl.classList.remove('gift-card-pdp__input--error');
  }
}

class GiftCardAmountSelector {
  constructor(section) {
    this.section = section;
    this.amountsContainer = section.querySelector('[data-gift-card-amounts]');
    if (!this.amountsContainer) return;

    this.amountBtns = this.amountsContainer.querySelectorAll('.gift-card-pdp__amount-btn');
    this.variantIdInput = section.querySelector('input[name="id"]');
    this.recipientForm = section.querySelector('recipient-form');

    this.bindEvents();
  }

  bindEvents() {
    this.amountBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.selectAmount(btn));
    });
  }

  selectAmount(btn) {
    this.amountBtns.forEach((b) => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    var variantId = btn.getAttribute('data-gift-variant-id');
    if (this.variantIdInput && variantId) {
      this.variantIdInput.value = variantId;
    }

    // Keep recipient-form variant ID in sync
    if (this.recipientForm && variantId) {
      this.recipientForm.dataset.productVariantId = variantId;
      if (this.recipientForm.currentProductVariantId !== undefined) {
        this.recipientForm.currentProductVariantId = variantId;
      }
    }
  }
}

// Delivery date field: a typeable text input paired with a hidden native
// date picker. The text input shows a caret so shoppers can type YYYY-MM-DD
// directly; the calendar icon on the right opens the native picker. Either
// way the value is normalized to YYYY-MM-DD and snapped to today if past.
function enforceFutureDeliveryDate(input) {
  if (!input) return;

  var pad = function (n) { return String(n).padStart(2, '0'); };
  var todayStr = function () {
    var d = new Date();
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  };

  var field = input.closest('.gift-card-pdp__date-field');
  var picker = field ? field.querySelector('[data-gift-card-date-picker]') : null;

  if (picker) {
    picker.min = todayStr();
    if (input.value) picker.value = input.value;

    // User chose a date in the native picker → reflect into the visible field.
    picker.addEventListener('change', function () {
      if (!picker.value) return;
      input.value = picker.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Single source of truth for opening the picker. showPicker() is the
    // modern API (Chrome 99+, Firefox 101+, Safari 16.4+); fall back to a
    // synthetic click for older browsers.
    var openPicker = function () {
      if (typeof picker.showPicker === 'function') {
        try { picker.showPicker(); return; } catch (_) { /* fall through */ }
      }
      try { picker.click(); } catch (_) { /* no-op */ }
    };

    // Calendar icon button → always opens the picker.
    var trigger = field.querySelector('[data-gift-card-date-trigger]');
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openPicker();
      });
    }

    // Match the old native-date UX: clicking into the field also opens the
    // picker — but only once per focus session, so clicking again to reposition
    // the caret while editing doesn't keep re-opening it. Re-arms when focus
    // leaves the field.
    var pickerArmed = true;
    input.addEventListener('mousedown', function () {
      if (!pickerArmed) return;
      pickerArmed = false;
      setTimeout(openPicker, 0);
    });
    document.addEventListener('click', function (e) {
      if (field && !field.contains(e.target)) pickerArmed = true;
    });
  }

  // Auto-format as the user types: digits only, dashes auto-inserted at
  // positions 4 and 7 so the value lands as YYYY-MM-DD without extra effort.
  input.addEventListener('input', function () {
    var digits = input.value.replace(/\D/g, '').slice(0, 8);
    var out = digits;
    if (digits.length > 4) out = digits.slice(0, 4) + '-' + digits.slice(4);
    if (digits.length > 6) out = digits.slice(0, 4) + '-' + digits.slice(4, 6) + '-' + digits.slice(6);
    if (input.value !== out) input.value = out;
  });

  // On commit, snap a past date forward to today and sync the hidden picker.
  input.addEventListener('change', function () {
    var today = todayStr();
    if (input.value && /^\d{4}-\d{2}-\d{2}$/.test(input.value) && input.value < today) {
      input.value = today;
    }
    if (picker) {
      picker.min = today;
      if (input.value) picker.value = input.value;
    }
  });
}

// After a successful gift-card add, behave like the other custom PDPs:
// don't redirect — disable the button as "ADDED TO CART", show the tooltip,
// and refresh the header cart count.
function handleGiftCardAdded(section) {
  var btn = section.querySelector('.product-detail__register-btn');

  if (btn) {
    if (window.cartStateManager && typeof window.cartStateManager.disableButton === 'function') {
      window.cartStateManager.disableButton(btn, 'ADDED TO CART');
    } else {
      btn.classList.add('is-disabled', 'is-added-to-cart');
      var textEl = btn.querySelector('.button__text');
      if (textEl) textEl.textContent = 'ADDED TO CART';
      var arrowEl = btn.querySelector('.button__icon');
      if (arrowEl) arrowEl.style.display = 'none';
    }
  }

  // Refresh cart count + show the "added to cart" tooltip (defined in
  // signature-product-section.js, which loads before this file).
  fetch('/cart.js', { credentials: 'same-origin' })
    .then(function (res) { return res.json(); })
    .then(function (cart) {
      if (typeof updateCartCount === 'function') updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    })
    .catch(function () { /* ignore */ })
    .finally(function () {
      if (typeof showAddedToCartMessage === 'function') showAddedToCartMessage();
    });
}

// Lightweight stand-in for the cart-drawer/notification object that
// product-form.js expects. It lets product-form.js run its normal AJAX add
// (with all recipient properties + error handling) but routes the success path
// into our custom flow instead of redirecting or rendering a cart drawer.
function makeGiftCardCartShim(section) {
  var dummyClassList = document.createElement('div').classList;
  return {
    classList: dummyClassList,
    getSectionsToRender: function () { return []; },
    setActiveElement: function () {},
    renderContents: function () {
      handleGiftCardAdded(section);
    },
  };
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.gift-card-pdp').forEach(function (section) {
    new GiftCardAmountSelector(section);
    new GiftCardFormValidator(section);
    enforceFutureDeliveryDate(section.querySelector('[data-gift-card-delivery-date]'));

    // Replace the redirect-to-cart behavior with the shared add-to-cart UX
    // (button disables + tooltip, no page redirect).
    var productForm = section.querySelector('product-form[data-redirect-to-cart="true"]');
    if (productForm) {
      productForm.cart = makeGiftCardCartShim(section);
    }
  });
});
